import { legacyApiAuth, monopolioApi, wiggotAPI } from '@wiggot/apis'
import { defaultApiBasic } from '@wiggot/apis/new-apis'
import { convertSnakeCaseToCamelCase } from '@wiggot/web-components/utils'
import type {
  IResponseService,
  IMonopolioProperty,
  IPropertyDetailEditV2,
  IResponsePropertyV2
} from '@wiggot/interfaces'
import {
  IPropertyDetail,
  IUser,
  IMonopolioPropertyMetrics
} from '@wiggot/interfaces'
import { IPropertyLead } from '@/interfaces'

import {
  IFetchServerResponse,
  fetchServerXAuthWithBinaryRole
} from '@wiggot/utils'
import { getConfigPdf } from '@wiggot/property-details/src/services/property-detail.service'

const WIGGOT_API_LEGACY = process.env.WIGGOT_API_LEGACY
const WIGGOT_API = process.env.WIGGOT_API

interface IMonopolioGetPropertyDetailResponse {
  message: string
  data: IMonopolioProperty
}

export interface IMonopolioPropertyDetailResponse {
  isSuccess: boolean
  data?: IMonopolioProperty
  error?: any
}

export interface IMonopolioPhoto {
  listingId: string
  order: number
  photo: string
}
export interface IMonopolioPhotoResponse {
  isSuccess: boolean
  data?: IMonopolioPhoto[]
  error?: any
  message?: string
}

export interface IGetPropertyDetailResponse {
  isSuccess: boolean
  property?: IPropertyDetail | null
  propertyV2?: IPropertyDetailEditV2
  monopolioMetrics?: IMonopolioPropertyMetrics
  errorCode?: string
}

export interface IPropertyDetailNew extends IPropertyDetail {
  builtYear: number | null
  createdAt: string | null
  updatedAt: string | null
}

export interface IProperty {
  property: IPropertyDetailNew
  user: IUser
  imagesIds?: string[]
  amenitiesUniqueIds?: string[]
}

interface ICustomDatasheet {
  propertyIds: string[]
  customImagesId: string[]
  customTitle: string
  customDescription: string
  customCoverImage: string
}

interface IPostCustomDatasheetData {
  property_id: string[]
  custom_images_id: string[]
  custom_title: string
  custom_description: string
  custom_cover_image: string
}

export const getUser = async (
  userId: string
): Promise<IResponseService<IUser>> => {
  try {
    const response = await fetch(
      `${WIGGOT_API_LEGACY}/v2.0.2/user/info/${userId}`
    ).then((res) => res.json())

    return {
      isSuccess: true,
      data: convertSnakeCaseToCamelCase(response)
    }
  } catch (error: any) {
    return {
      isSuccess: false,
      error
    }
  }
}

export const fetchPropertyDetail = async ({
  id
}: {
  token?: string
  id: string
}): Promise<IFetchServerResponse> => {
  try {
    const newProperty = await wiggotAPI.get<IResponseService<IProperty>>(
      `/properties/${id}`
    )
    const user = await getUser(
      newProperty?.data?.property?.assignedUserId || ''
    )
    const currentYear = new Date().getFullYear()
    const hastBuiltYear = Boolean(newProperty?.data?.property?.builtYear)

    return {
      data: {
        ...newProperty?.data?.property,
        agencyId: user?.data?.agencyId,
        agencyName: user?.data?.agencyName,
        agencyLogo: user?.data?.agencyLogo,
        amenities: newProperty?.data?.amenitiesUniqueIds,
        antiquityYears: hastBuiltYear
          ? currentYear - (newProperty?.data?.property?.builtYear ?? 0)
          : null,
        brokerName: user?.data?.fullname,
        brokerPhoto: user?.data?.photo,
        contactEmail: user?.data?.email,
        contactPhone: user?.data?.phoneNumber,
        images: newProperty?.data?.imagesIds,
        modifiedAt: newProperty?.data?.property?.updatedAt,
        profileId: user?.data?.profileId,
        propertyId: newProperty?.data?.property?.legacyId,
        userId: user?.data?.userId
      },
      isError: false,
      status: 200
    }
  } catch (error: any) {
    console.error('Error fetching property detail', error)
    return {
      data: {},
      status: error?.response?.status,
      isError: true
    }
  }
}

export const fetchPropertyDataV2 = async ({
  propertyId,
  userId,
  binaryRole,
  agencyId
}: {
  propertyId: string
  userId: string
  binaryRole: number
  agencyId?: string
}): Promise<{
  data: { data: IPropertyDetailEditV2 }
  status: number
  isError: boolean
}> => {
  const res = await fetchServerXAuthWithBinaryRole(
    `${WIGGOT_API}/properties/${propertyId}/edit`,
    userId,
    binaryRole,
    agencyId
  )

  return {
    data: res?.data,
    status: res?.status,
    isError: res?.isError
  }
}

export const fetchMonopolioPropertyDetail = async ({
  id
}: {
  id: string
}): Promise<IMonopolioPropertyDetailResponse> => {
  try {
    const response: IMonopolioGetPropertyDetailResponse =
      await monopolioApi.get<IMonopolioGetPropertyDetailResponse>(
        `/listings-properties/public/properties/${id}`
      )

    if (response?.message === 'Property retrieved') {
      return {
        isSuccess: true,
        data: response?.data
      }
    } else {
      return {
        isSuccess: false,
        error: response?.data
      }
    }
  } catch (error) {
    return {
      isSuccess: false,
      error
    }
  }
}

export const fetchMonopolioPhotos = async ({
  id
}: {
  id: string
}): Promise<IMonopolioPhotoResponse> => {
  try {
    const response: IMonopolioPhotoResponse =
      await monopolioApi.get<IMonopolioPhotoResponse>(
        `/listings-properties/public/photos/ads/${id}?withoutDomain=false`
      )

    if (response?.message === 'Property photos retrieved') {
      return {
        isSuccess: true,
        data: response?.data
      }
    } else {
      return {
        isSuccess: false,
        error: [response?.data]
      }
    }
  } catch (error) {
    return {
      isSuccess: false,
      error
    }
  }
}

export interface IPropertyLeadParams {
  propertyId: string
  userId: number
  wiggotUserId: number
  name: string
  email: string
  phone: string
  message: string
  incomingUrl: string
  phoneCode?: string
}

export interface IPropertyLeadBody {
  propertyId: string
  userId: number
  wiggotUserId: number
  name: string
  email: string
  phone: string
  message: string
  incomingUrl: string
  source: string
  phoneCode: string
  isBroker: boolean
}

export const propertyLead = async ({
  propertyId,
  userId,
  wiggotUserId,
  name,
  email,
  phone,
  message,
  phoneCode = '52',
  incomingUrl
}: IPropertyLeadParams): Promise<IResponseService<IPropertyLead[]>> => {
  try {
    const phoneNumber =
      phone?.length > 10 ? phone.slice(phone?.length - 10) : phone

    const response = await defaultApiBasic.post<
      IPropertyLead[],
      IPropertyLeadBody
    >(`/lead-management/leads`, {
      wiggotUserId,
      propertyId,
      userId,
      email,
      incomingUrl,
      message,
      name,
      phone: phoneNumber,
      source: 'WIGGOT',
      phoneCode,
      isBroker: true
    })

    return {
      isSuccess: true,
      data: response
    }
  } catch (error: any) {
    return {
      isSuccess: false,
      error
    }
  }
}
export const getProperty = async (
  property_id: string
): Promise<IResponseService<IResponsePropertyV2>> => {
  const newProperty = await wiggotAPI.get<
    IResponseService<IResponsePropertyV2>
  >(`/properties/${property_id}`)
  try {
    return {
      isSuccess: true,
      data: newProperty?.data
    }
  } catch (error: any) {
    return {
      isSuccess: false,
      error
    }
  }
}
export const getPropertyDetail = async ({
  id
}: {
  id: string
}): Promise<IResponseService<IResponsePropertyV2>> => {
  const response = await getProperty(id)
  if (response?.isSuccess) {
    return {
      isSuccess: true,
      data: response?.data
    }
  }
  const errorCode: string = response?.error?.toString() ?? 'ERR-UNKNOWN'
  return {
    isSuccess: false,
    error: `WIG-${errorCode}`,
  }
}

export const fetchConfigPdf = async (reference: string) => {
  try {
    const configData = await getConfigPdf(reference)
    return configData
  } catch (error: any) {
    return {
      isSuccess: false,

      error
    }
  }
}

export const PostCustomDatasheet = async ({
  propertyIds,
  customImagesId,
  customTitle,
  customDescription,
  customCoverImage
}: ICustomDatasheet): Promise<IResponseService<{ datasheetId: string }>> => {
  const response = await legacyApiAuth.post<
    { datasheet_id: string },
    IPostCustomDatasheetData
  >(`/v2.0.2/datasheet/share_custom`, {
    property_id: propertyIds,
    custom_images_id: customImagesId,
    custom_title: customTitle,
    custom_description: customDescription,
    custom_cover_image: customCoverImage
  })
  try {
    return {
      isSuccess: true,
      data: convertSnakeCaseToCamelCase(response)
    }
  } catch (error: any) {
    return {
      isSuccess: false,
      error
    }
  }
}

export interface IPropertyRegisterCopyResponse {
  code: number
  message: string
}

export interface IPropertyRegisterCopyPayload {
  profile_id?: string
  datasheet_id?: string
  without_contact?: boolean
}

export interface IPropertyRegisterCopyParams {
  propertyId?: string
  profileId?: string
  datasheetId?: string
  withoutContact?: boolean
}

export const propertyRegisterCopy = async ({
  propertyId,
  profileId,
  datasheetId,
  withoutContact
}: IPropertyRegisterCopyParams): Promise<
  IResponseService<IPropertyRegisterCopyResponse>
> => {
  try {
    const body: IPropertyRegisterCopyPayload = {
      profile_id: profileId,
      datasheet_id: datasheetId,
      without_contact: withoutContact
    }

    const response = await legacyApiAuth.post<
      IPropertyRegisterCopyResponse,
      IPropertyRegisterCopyPayload
    >(`/v2.0.2/stats/properties/${propertyId}/copy_url_to_share`, body)

    return {
      isSuccess: true,
      data: response
    }
  } catch (error: any) {
    return {
      isSuccess: false,
      error
    }
  }
}
